import React from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";

interface TenantData {
	tenant: {
		id: string | null;
		name: string;
		email: string;
		phone: string;
		profilePicture: string | null;
	};
	lease: {
		id: string;
		lease_code?: string;
		property_address?: string;
		start_date: string;
		end_date: string;
		monthly_rent: number;
		security_deposit?: number;
		status: string;
		renewal_deadline?: string;
	};
	property?: {
		id: string;
		name: string;
		description?: string;
		property_type?: string;
		address: string;
		city?: string;
		state?: string;
		zip_code?: string;
		bedrooms?: number;
		bathrooms?: number;
		square_feet?: number;
		images?: string[];
		features?: string[];
	};
	payments: {
		currentBalance: number;
		nextAmount?: number;
		nextPaymentDue: string;
		paymentHistory: Array<{
			id: string;
			amount: number;
			date: string;
			status: string;
			method: string;
			reference?: string;
		}>;
		history: Array<{
			id: string;
			amount: number;
			date: string;
			status: string;
			method: string;
			reference?: string;
		}>;
	};
	maintenance: {
		activeRequests: number;
		completedRequests: number;
		averageResponseTime: string;
		recentRequests: Array<{
			id: string;
			title: string;
			description?: string;
			status: string;
			priority: string;
			date: string;
			submittedDate?: string;
		}>;
	};
	notifications: Array<{
		id: string;
		type: string;
		title: string;
		message: string;
		date: string;
		read: boolean;
	}>;
}

type SectionType = "dashboard" | "lease" | "payments" | "maintenance";

interface Announcement {
	id: string;
	title: string;
	content: string;
	priority: "info" | "warning" | "success" | "urgent";
	created_at: string;
	action_link?: string;
	action_text?: string;
}

interface DashboardOverviewProps {
	tenantData: TenantData | null;
	onSectionChange?: (section: SectionType) => void;
	announcements?: Announcement[];
}

const DashboardOverview: React.FC<DashboardOverviewProps> = ({
	tenantData,
	onSectionChange,
	announcements = [],
}) => {
	const navigate = useNavigate();
	const formatCurrency = (amount: number): string => {
		return new Intl.NumberFormat("en-NG", {
			style: "currency",
			currency: "NGN",
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(amount);
	};

	const formatDate = (dateString: string | undefined): string => {
		if (!dateString) return "N/A";
		const date = new Date(dateString);
		if (isNaN(date.getTime())) return "N/A";
		return date.toLocaleDateString("en-US", {
			month: "short",
			day: "numeric",
			year: "numeric",
		});
	};

	const getDaysUntilDue = (dueDateString: string | undefined): number => {
		if (!dueDateString) return 0;
		const dueDate = new Date(dueDateString);
		const today = new Date();
		// Reset time to midnight for accurate day calculation
		today.setHours(0, 0, 0, 0);
		dueDate.setHours(0, 0, 0, 0);
		if (isNaN(dueDate.getTime())) return 0;
		const diffTime = dueDate.getTime() - today.getTime();
		const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
		return diffDays;
	};

	const getStatusBadge = (status: string | undefined): string => {
		const baseClasses =
			"px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap";

		// Handle enum values like "MaintenanceStatus.PENDING"
		const statusValue = status?.includes(".")
			? status.split(".").pop()?.toLowerCase()
			: status?.toLowerCase();

		switch (statusValue) {
			case "active":
				return `${baseClasses} bg-success/10 text-success`;
			case "expired":
				return `${baseClasses} bg-error/10 text-error`;
			case "pending":
				return `${baseClasses} bg-primary/10 text-primary`;
			case "in_progress":
			case "inprogress":
				return `${baseClasses} bg-warning/10 text-warning`;
			case "completed":
				return `${baseClasses} bg-success/10 text-success`;
			case "submitted":
				return `${baseClasses} bg-primary/10 text-primary`;
			default:
				return `${baseClasses} bg-muted text-muted-foreground`;
		}
	};

	const formatStatus = (status: string | undefined): string => {
		if (!status) return "Unknown";
		// Handle enum values like "MaintenanceStatus.PENDING"
		const statusValue = status.includes(".") ? status.split(".").pop() : status;
		return (statusValue || "")
			.replace(/_/g, " ")
			.replace(/\b\w/g, (l) => l.toUpperCase());
	};

	const formatPriority = (priority: string | undefined): string => {
		if (!priority) return "";
		// Handle enum values like "MaintenancePriority.MEDIUM"
		const priorityValue = priority.includes(".")
			? priority.split(".").pop()
			: priority;
		const value = priorityValue || "";
		return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
	};

	const getPriorityIcon = (priority: string): string => {
		switch (priority.toLowerCase()) {
			case "info":
				return "Info";
			case "warning":
				return "AlertTriangle";
			case "success":
				return "CheckCircle";
			case "urgent":
				return "AlertCircle";
			default:
				return "Info";
		}
	};

	const getPriorityColor = (priority: string): string => {
		switch (priority.toLowerCase()) {
			case "info":
				return "text-primary";
			case "warning":
				return "text-warning";
			case "success":
				return "text-success";
			case "urgent":
				return "text-error";
			default:
				return "text-primary";
		}
	};

	const getPriorityBgColor = (priority: string): string => {
		switch (priority.toLowerCase()) {
			case "info":
				return "bg-primary/5 border-primary/20";
			case "warning":
				return "bg-warning/5 border-warning/20";
			case "success":
				return "bg-success/5 border-success/20";
			case "urgent":
				return "bg-error/5 border-error/20";
			default:
				return "bg-primary/5 border-primary/20";
		}
	};

	const daysUntilRentDue = getDaysUntilDue(tenantData?.payments?.nextPaymentDue);
	const daysUntilLeaseExpiry = getDaysUntilDue(tenantData?.lease?.end_date);

	return (
		<div className="space-y-6">
			{/* Quick Status Cards */}
			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
				{/* Lease Status */}
				<div className="bg-card border border-border rounded-lg p-4 card-shadow">
					<div className="flex items-center space-x-3">
						<div className="p-2 bg-success/10 rounded-lg">
							<Icon name="FileText" size={20} className="text-success" />
						</div>
						<div>
							<p className="text-sm font-medium text-foreground">
								Lease Status
							</p>
							<p className="text-xs text-muted-foreground">
								<span className={getStatusBadge(tenantData?.lease?.status)}>
									{tenantData?.lease?.status
										? tenantData.lease.status.charAt(0).toUpperCase() +
										  tenantData.lease.status.slice(1)
										: "N/A"}
								</span>
							</p>
						</div>
					</div>
				</div>

				{/* Current Balance */}
				<div className="bg-card border border-border rounded-lg p-4 card-shadow">
					<div className="flex items-center space-x-3">
						<div
							className={`p-2 rounded-lg ${
								(tenantData?.payments?.currentBalance ?? 0) > 0
									? "bg-error/10"
									: "bg-success/10"
							}`}
						>
							<Icon
								name="Currency"
								size={20}
								className={
									(tenantData?.payments?.currentBalance ?? 0) > 0
										? "text-error"
										: "text-success"
								}
							/>
						</div>
						<div>
							<p className="text-sm font-medium text-foreground">
								Current Balance
							</p>
							<p className="text-lg font-bold text-foreground">
								{formatCurrency(tenantData?.payments?.currentBalance || 0)}
							</p>
						</div>
					</div>
				</div>

				{/* Next Payment */}
				<div className="bg-card border border-border rounded-lg p-4 card-shadow">
					<div className="flex items-center space-x-3">
						<div
							className={`p-2 rounded-lg ${
								daysUntilRentDue <= 3 ? "bg-warning/10" : "bg-primary/10"
							}`}
						>
							<Icon
								name="Calendar"
								size={20}
								className={
									daysUntilRentDue <= 3 ? "text-warning" : "text-primary"
								}
							/>
						</div>
						<div>
							<p className="text-sm font-medium text-foreground">
								Next Payment
							</p>
							<p className="text-xs text-muted-foreground">
								{daysUntilRentDue > 0
									? `Due in ${daysUntilRentDue} days`
									: "Due today"}
							</p>
						</div>
					</div>
				</div>

				{/* Maintenance Requests */}
				<div className="bg-card border border-border rounded-lg p-4 card-shadow">
					<div className="flex items-center space-x-3">
						<div className="p-2 bg-warning/10 rounded-lg">
							<Icon name="Wrench" size={20} className="text-warning" />
						</div>
						<div>
							<p className="text-sm font-medium text-foreground">
								Active Requests
							</p>
							<p className="text-lg font-bold text-foreground">
								{tenantData?.maintenance?.activeRequests || 0}
							</p>
						</div>
					</div>
				</div>
			</div>

			{/* Main Dashboard Content */}
			<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
				{/* Payment Information */}
				<div className="bg-card border border-border rounded-lg card-shadow">
					<div className="p-4 border-b border-border">
						<div className="flex items-center justify-between">
							<h3 className="font-semibold text-foreground">
								Payment Information
							</h3>
							<div className="flex items-center gap-2">
								<Button
									variant="outline"
									size="sm"
									onClick={() => navigate("/payment-proof-upload")}
									className="flex items-center gap-1.5"
								>
									<Icon name="Upload" size={14} />
									<span className="hidden sm:inline">Upload Proof</span>
								</Button>
								<Button
									variant="ghost"
									size="sm"
									onClick={() => onSectionChange?.("payments")}
									className="text-primary hover:text-primary/80"
								>
									View All
								</Button>
							</div>
						</div>
					</div>

					<div className="p-4 space-y-4">
						{/* Next Payment Due */}
						<div className="p-3 bg-primary/5 border border-primary/20 rounded-lg">
							<div className="flex items-center justify-between mb-2">
								<h4 className="font-medium text-foreground">
									Next Rent Payment
								</h4>
								<span
									className={`text-sm font-medium ${
										daysUntilRentDue <= 3 ? "text-warning" : "text-primary"
									}`}
								>
									{daysUntilRentDue > 0
										? `${daysUntilRentDue} days left`
										: "Due today"}
								</span>
							</div>

							<div className="flex items-center justify-between">
								<div>
									<p className="text-2xl font-bold text-foreground">
										{formatCurrency(tenantData?.lease?.monthly_rent || 0)}
									</p>
									<p className="text-sm text-muted-foreground">
										Due: {formatDate(tenantData?.payments?.nextPaymentDue)}
									</p>
								</div>

								<Button
									onClick={() => onSectionChange?.("payments")}
									className="flex items-center space-x-2"
								>
									<Icon name="CreditCard" size={16} />
									<span>Pay Now</span>
								</Button>
							</div>
						</div>

						{/* Upload Payment Proof Button */}
						<div className="p-3 bg-muted/30 border border-border rounded-lg">
							<div className="flex items-center justify-between">
								<div className="flex items-center space-x-3">
									<div className="p-2 bg-primary/10 rounded-lg">
										<Icon name="Upload" size={16} className="text-primary" />
									</div>
									<div>
										<h4 className="text-sm font-medium text-foreground">
											Upload Payment Proof
										</h4>
										<p className="text-xs text-muted-foreground">
											Submit your payment evidence
										</p>
									</div>
								</div>
								<Button
									variant="outline"
									size="sm"
									onClick={() => navigate("/payment-proof-upload")}
									className="flex items-center space-x-2"
								>
									<Icon name="Upload" size={14} />
									<span>Upload</span>
								</Button>
							</div>
						</div>

						{/* Recent Payments */}
						<div>
							<h4 className="font-medium text-foreground mb-3">
								Recent Payments
							</h4>
							<div className="space-y-2">
								{tenantData?.payments?.history?.slice(0, 3).map((payment) => {
									// Normalize status - handle enum values like "PaymentStatus.PENDING"
									const normalizedStatus = payment.status?.includes(".")
										? payment.status.split(".").pop()?.toLowerCase() ||
										  "pending"
										: payment.status?.toLowerCase() || "pending";

									return (
										<div
											key={payment.id}
											className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
										>
											<div className="flex-1 min-w-0">
												<div className="flex items-center gap-2 mb-1">
													<p className="text-sm font-medium text-foreground">
														{formatDate(payment.date)}
													</p>
													<span className="text-muted-foreground">•</span>
													<p className="text-sm font-bold text-foreground">
														{formatCurrency(payment.amount)}
													</p>
												</div>
												{payment.method && (
													<p className="text-xs text-muted-foreground capitalize">
														{payment.method.replace("_", " ")}
														{payment.reference && ` • ${payment.reference}`}
													</p>
												)}
											</div>
											<div className="ml-4 flex-shrink-0">
												<span className={getStatusBadge(normalizedStatus)}>
													{formatStatus(normalizedStatus)}
												</span>
											</div>
										</div>
									);
								})}
							</div>
						</div>
					</div>
				</div>

				{/* Lease Information */}
				<div className="bg-card border border-border rounded-lg card-shadow">
					<div className="p-4 border-b border-border">
						<div className="flex items-center justify-between">
							<h3 className="font-semibold text-foreground">
								Lease Information
							</h3>
							<Button
								variant="ghost"
								size="sm"
								onClick={() => onSectionChange?.("lease")}
								className="text-primary hover:text-primary/80"
							>
								View Details
							</Button>
						</div>
					</div>

					<div className="p-4 space-y-4">
						{/* Lease Overview */}
						<div className="space-y-3">
							<div className="flex items-center justify-between">
								<span className="text-sm text-muted-foreground">Lease ID</span>
								<span
									className="text-sm font-medium text-foreground font-mono truncate max-w-[200px]"
									title={tenantData?.lease?.lease_code || tenantData?.lease?.id}
								>
									{tenantData?.lease?.lease_code ||
										tenantData?.lease?.id ||
										"N/A"}
								</span>
							</div>

							<div className="flex items-center justify-between">
								<span className="text-sm text-muted-foreground">
									Monthly Rent
								</span>
								<span className="text-sm font-bold text-foreground">
									{formatCurrency(tenantData?.lease?.monthly_rent || 0)}
								</span>
							</div>

							<div className="flex items-center justify-between">
								<span className="text-sm text-muted-foreground">
									Lease Period
								</span>
								<span className="text-sm font-medium text-foreground">
									{tenantData?.lease?.start_date && tenantData?.lease?.end_date
										? `${formatDate(
												tenantData.lease.start_date,
										  )} - ${formatDate(tenantData.lease.end_date)}`
										: "N/A"}
								</span>
							</div>

							<div className="flex items-center justify-between">
								<span className="text-sm text-muted-foreground">
									Days Until Expiry
								</span>
								<span
									className={`text-sm font-medium ${
										daysUntilLeaseExpiry <= 90
											? "text-warning"
											: daysUntilLeaseExpiry <= 180
											? "text-primary"
											: "text-foreground"
									}`}
								>
									{daysUntilLeaseExpiry}{" "}
									{daysUntilLeaseExpiry === 1 ? "day" : "days"}
								</span>
							</div>
						</div>

						{/* Renewal Notice */}
						{tenantData?.lease?.renewal_deadline &&
							daysUntilLeaseExpiry <= 120 && (
								<div className="p-3 bg-warning/5 border border-warning/20 rounded-lg">
									<div className="flex items-start space-x-2">
										<Icon
											name="AlertTriangle"
											size={16}
											className="text-warning mt-0.5"
										/>
										<div>
											<h4 className="text-sm font-medium text-foreground">
												Lease Renewal Available
											</h4>
											<p className="text-xs text-muted-foreground mt-1">
												Your lease expires in {daysUntilLeaseExpiry} days.
												Contact management to discuss renewal options.
											</p>
											<Button
												variant="outline"
												size="sm"
												className="mt-2 text-xs"
												onClick={() => navigate("/messages")}
											>
												Contact Management
											</Button>
										</div>
									</div>
								</div>
							)}
					</div>
				</div>

				{/* Property Details */}
				{tenantData?.property && (
					<div className="bg-card border border-border rounded-lg card-shadow">
						<div className="p-4 border-b border-border">
							<h3 className="font-semibold text-foreground">
								Property Details
							</h3>
						</div>

						<div className="p-4 space-y-4">
							{/* Property Image */}
							{tenantData.property.images && tenantData.property.images.length > 0 && (
								<div className="w-full h-48 rounded-lg overflow-hidden">
									<img
										src={tenantData.property.images[0]}
										alt={tenantData.property.name}
										className="w-full h-full object-cover"
									/>
								</div>
							)}

							{/* Property Name */}
							<div>
								<h4 className="text-lg font-semibold text-foreground">
									{tenantData.property.name}
								</h4>
								<p className="text-sm text-muted-foreground mt-1">
									{tenantData.property.address}
									{tenantData.property.city && `, ${tenantData.property.city}`}
									{tenantData.property.state && `, ${tenantData.property.state}`}
								</p>
							</div>

							{/* Property Specs */}
							<div className="grid grid-cols-3 gap-3">
								<div className="flex flex-col items-center p-3 bg-muted/30 rounded-lg">
									<Icon name="Bed" size={20} className="text-primary mb-1" />
									<span className="text-sm font-medium text-foreground">
										{tenantData.property.bedrooms || 0}
									</span>
									<span className="text-xs text-muted-foreground">Bedrooms</span>
								</div>
								<div className="flex flex-col items-center p-3 bg-muted/30 rounded-lg">
									<Icon name="Bath" size={20} className="text-primary mb-1" />
									<span className="text-sm font-medium text-foreground">
										{tenantData.property.bathrooms || 0}
									</span>
									<span className="text-xs text-muted-foreground">Bathrooms</span>
								</div>
								<div className="flex flex-col items-center p-3 bg-muted/30 rounded-lg">
									<Icon name="Maximize" size={20} className="text-primary mb-1" />
									<span className="text-sm font-medium text-foreground">
										{tenantData.property.square_feet || 0}
									</span>
									<span className="text-xs text-muted-foreground">sq ft</span>
								</div>
							</div>

							{/* Property Type */}
							{tenantData.property.property_type && (
								<div className="flex items-center justify-between">
									<span className="text-sm text-muted-foreground">Type</span>
									<span className="text-sm font-medium text-foreground capitalize">
										{tenantData.property.property_type}
									</span>
								</div>
							)}

							{/* Property Features */}
							{tenantData.property.features && tenantData.property.features.length > 0 && (
								<div>
									<span className="text-sm font-medium text-foreground mb-2 block">
										Features
									</span>
									<div className="flex flex-wrap gap-2">
										{tenantData.property.features.slice(0, 6).map((feature, index) => (
											<span
												key={index}
												className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-md"
											>
												{feature}
											</span>
										))}
										{tenantData.property.features.length > 6 && (
											<span className="px-2 py-1 bg-muted/30 text-muted-foreground text-xs rounded-md">
												+{tenantData.property.features.length - 6} more
											</span>
										)}
									</div>
								</div>
							)}
						</div>
					</div>
				)}
			</div>

			{/* Maintenance Requests */}
			<div className="bg-card border border-border rounded-lg card-shadow overflow-hidden">
				<div className="p-4 border-b border-border">
					<div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
						<h3 className="font-semibold text-foreground">
							Maintenance Requests
						</h3>
						<div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
							<Button
								variant="ghost"
								size="sm"
								onClick={() => onSectionChange?.("maintenance")}
								className="text-primary hover:text-primary/80 w-full sm:w-auto"
							>
								View All
							</Button>
							<Button
								size="sm"
								onClick={() => onSectionChange?.("maintenance")}
								className="flex items-center justify-center space-x-2 w-full sm:w-auto"
							>
								<Icon name="Plus" size={16} />
								<span>New Request</span>
							</Button>
						</div>
					</div>
				</div>

				<div className="p-4">
					{tenantData?.maintenance?.recentRequests &&
					tenantData.maintenance.recentRequests.length > 0 ? (
						<div className="space-y-3">
							{tenantData.maintenance.recentRequests
								.slice(0, 3)
								.map((request) => {
									const statusValue = request.status?.includes(".")
										? request.status.split(".").pop()?.toLowerCase()
										: request.status?.toLowerCase();
									return (
										<div
											key={request.id}
											className="p-3 bg-muted/30 rounded-lg border border-border"
										>
											<div className="flex flex-col sm:flex-row sm:items-start gap-3">
												<div className="flex items-start space-x-3 flex-1 min-w-0">
													<div
														className={`p-2 rounded flex-shrink-0 ${
															statusValue === "completed"
																? "bg-success/10"
																: statusValue === "in_progress" ||
																  statusValue === "inprogress"
																? "bg-warning/10"
																: "bg-primary/10"
														}`}
													>
														<Icon
															name="Wrench"
															size={16}
															className={
																statusValue === "completed"
																	? "text-success"
																	: statusValue === "in_progress" ||
																	  statusValue === "inprogress"
																	? "text-warning"
																	: "text-primary"
															}
														/>
													</div>
													<div className="flex-1 min-w-0">
														<h4 className="font-medium text-foreground truncate">
															{request.title}
														</h4>
														{request.description && (
															<p className="text-sm text-muted-foreground mt-1 line-clamp-2 break-words">
																{request.description}
															</p>
														)}
														<p className="text-xs text-muted-foreground mt-2">
															Submitted:{" "}
															{formatDate(
																request.submittedDate || request.date,
															)}
														</p>
													</div>
												</div>

												<div className="flex flex-col sm:items-end gap-2 flex-shrink-0">
													<span className={getStatusBadge(request.status)}>
														{formatStatus(request.status)}
													</span>
													{request.priority && (
														<p className="text-xs text-muted-foreground whitespace-nowrap">
															{formatPriority(request.priority)} priority
														</p>
													)}
												</div>
											</div>
										</div>
									);
								})}
						</div>
					) : (
						<div className="text-center py-8">
							<Icon
								name="Wrench"
								size={48}
								className="mx-auto mb-4 text-muted-foreground opacity-50"
							/>
							<h4 className="font-medium text-foreground mb-2">
								No maintenance requests
							</h4>
							<p className="text-muted-foreground mb-4">
								Submit a request if you need any repairs or maintenance.
							</p>
							<Button
								onClick={() => onSectionChange?.("maintenance")}
								className="flex items-center space-x-2"
							>
								<Icon name="Plus" size={16} />
								<span>Submit Request</span>
							</Button>
						</div>
					)}
				</div>
			</div>

			{/* Important Announcements */}
			<div className="bg-card border border-border rounded-lg card-shadow">
				<div className="p-4 border-b border-border">
					<h3 className="font-semibold text-foreground">
						Important Announcements
					</h3>
				</div>

				<div className="p-4">
					{announcements && announcements.length > 0 ? (
						<div className="space-y-3">
							{announcements.map((announcement) => (
								<div
									key={announcement.id}
									className={`p-3 ${getPriorityBgColor(
										announcement.priority,
									)} rounded-lg`}
								>
									<div className="flex items-start space-x-2">
										<Icon
											name={getPriorityIcon(announcement.priority) as any}
											size={16}
											className={`${getPriorityColor(
												announcement.priority,
											)} mt-0.5`}
										/>
										<div className="flex-1">
											<h4 className="text-sm font-medium text-foreground">
												{announcement.title}
											</h4>
											<p className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap">
												{announcement.content}
											</p>
											<div className="flex items-center justify-between mt-2">
												<p className="text-xs text-muted-foreground">
													Posted: {formatDate(announcement.created_at)}
												</p>
												{announcement.action_link &&
													announcement.action_text && (
														<a
															href={announcement.action_link}
															target="_blank"
															rel="noopener noreferrer"
															className="text-xs text-primary hover:underline"
														>
															{announcement.action_text} →
														</a>
													)}
											</div>
										</div>
									</div>
								</div>
							))}
						</div>
					) : (
						<div className="text-center py-8">
							<Icon
								name="Megaphone"
								size={48}
								className="mx-auto mb-4 text-muted-foreground opacity-50"
							/>
							<h4 className="font-medium text-foreground mb-2">
								No announcements
							</h4>
							<p className="text-muted-foreground text-sm">
								You're all caught up! Check back later for updates.
							</p>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default DashboardOverview;
